import classNames from 'classnames';
import Icon from '@shared/components/Icon/Icon';
import Link from 'next/link';
import styles from './LightRetouchTextLink.module.scss';

type LightRetouchTextLinkBaseProps = {
    children: React.ReactNode;
    /** Extra class(es) applied to the root — use for positioning overrides (e.g. `mt-auto`). */
    className?: string;
    /** Show the trailing arrow icon. Defaults to `true`. */
    showArrow?: boolean;
    /** `gold` — hover and active state use gold colour instead of the default secondary. */
    variant?: 'default' | 'gold';
    /** Marks the link as the current active page/route. */
    active?: boolean;
};

type LightRetouchTextLinkAnchorProps = LightRetouchTextLinkBaseProps &
    Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
        href: string;
        prefetch?: boolean;
    };

type LightRetouchTextLinkButtonProps = LightRetouchTextLinkBaseProps &
    React.ButtonHTMLAttributes<HTMLButtonElement> & {
        href?: undefined;
        prefetch?: never;
    };

type LightRetouchTextLinkProps = LightRetouchTextLinkAnchorProps | LightRetouchTextLinkButtonProps;

/**
 * Light Retouch text link with optional trailing arrow and animated underline reveal.
 * - With `href`  → renders as `<Link>`
 * - Without `href` → renders as `<button>` (e.g. open modal)
 */
const LightRetouchTextLink: React.FC<LightRetouchTextLinkProps> = ({
    children,
    className,
    showArrow = true,
    variant = 'default',
    active = false,
    href,
    prefetch,
    ...rest
}) => {
    const cls = classNames(styles.link, 'font-medium text-base', className, {
        [styles.gold]: variant === 'gold',
        [styles.active]: active,
    });

    const content = (
        <>
            <span className={styles.row}>
                {children}
                {showArrow && <Icon type="arrowRight" width={18} height={18} aria-hidden="true" />}
            </span>
            <span className={styles.underline} aria-hidden="true" />
        </>
    );

    if (href !== undefined) {
        return (
            <Link
                href={href}
                className={cls}
                prefetch={prefetch}
                {...(rest as Omit<
                    LightRetouchTextLinkAnchorProps,
                    | 'children'
                    | 'className'
                    | 'href'
                    | 'showArrow'
                    | 'variant'
                    | 'active'
                    | 'prefetch'
                >)}
            >
                {content}
            </Link>
        );
    }

    return (
        <button
            type="button"
            className={classNames(cls, styles.asButton)}
            {...(rest as Omit<
                LightRetouchTextLinkButtonProps,
                'children' | 'className' | 'href' | 'showArrow' | 'variant' | 'active' | 'prefetch'
            >)}
        >
            {content}
        </button>
    );
};

export default LightRetouchTextLink;
